/**
 * Hybrid Search Engine
 * Combines lexical (keyword) and semantic (meaning) search with intelligent ranking
 */

import { SearchItem } from './searchIndex';

// Type definitions
export interface SearchResult extends SearchItem {
  score: number;
  matchType: 'exact' | 'prefix' | 'contains' | 'semantic' | 'fuzzy';
  highlights: HighlightedText[];
  isPinned?: boolean;
}

export interface HighlightedText {
  field: 'title' | 'description' | 'keywords';
  text: string;
  matches: { start: number; end: number }[];
}

export interface SearchFilters {
  contentType?: string[];
  visibility?: 'all' | 'public' | 'internal';
}

export interface SynonymMap {
  [term: string]: { synonyms: string[]; weight: number };
}

// Edit distance calculation for typo tolerance (Levenshtein)
export const editDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

// Check if two strings are similar (for typo tolerance)
export const isSimilar = (query: string, text: string, threshold = 2): boolean => {
  const normalizedQuery = query.toLowerCase();
  const normalizedText = text.toLowerCase();
  
  // Short queries need exact or near-exact matches
  if (normalizedQuery.length <= 3) {
    return editDistance(normalizedQuery, normalizedText) <= 1;
  }
  
  // Longer queries can have more tolerance
  const maxDistance = Math.min(threshold, Math.floor(normalizedQuery.length / 3));
  return editDistance(normalizedQuery, normalizedText) <= maxDistance;
};

// Find matches in text for highlighting
export const findMatches = (text: string, query: string): { start: number; end: number }[] => {
  const matches: { start: number; end: number }[] = [];
  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase();
  
  let index = normalizedText.indexOf(normalizedQuery);
  while (index !== -1) {
    matches.push({ start: index, end: index + normalizedQuery.length });
    index = normalizedText.indexOf(normalizedQuery, index + 1);
  }
  
  return matches;
};

// Highlight matched text in a field
export const highlightField = (
  text: string, 
  query: string, 
  field: 'title' | 'description' | 'keywords'
): HighlightedText => {
  const matches = findMatches(text, query);
  return { field, text, matches };
};

// Generate highlighted HTML from matches
export const applyHighlights = (text: string, matches: { start: number; end: number }[]): string => {
  if (matches.length === 0) return text;
  
  // Sort matches by start position
  const sortedMatches = [...matches].sort((a, b) => a.start - b.start);
  
  let result = '';
  let lastIndex = 0;
  
  for (const match of sortedMatches) {
    result += text.slice(lastIndex, match.start);
    result += `<mark class="bg-primary/20 text-primary rounded px-0.5">${text.slice(match.start, match.end)}</mark>`;
    lastIndex = match.end;
  }
  
  result += text.slice(lastIndex);
  return result;
};

// Block type priority for ranking
const BLOCK_TYPE_PRIORITY: { [key: string]: number } = {
  product: 100,
  'case-study': 90,
  page: 80,
  feature: 70,
  category: 60,
  faq: 50,
  blog: 40,
  section: 30,
};

// Calculate semantic score using synonyms
export const calculateSemanticScore = (
  query: string,
  item: SearchItem,
  synonymMap: SynonymMap
): number => {
  const queryWords = query.toLowerCase().split(/\s+/);
  let semanticScore = 0;

  for (const word of queryWords) {
    const synonymEntry = synonymMap[word];
    if (synonymEntry) {
      const { synonyms, weight } = synonymEntry;
      
      // Check if any synonym matches item content
      const itemText = `${item.title} ${item.description} ${item.keywords.join(' ')}`.toLowerCase();
      
      for (const synonym of synonyms) {
        if (itemText.includes(synonym.toLowerCase())) {
          semanticScore += 50 * weight;
        }
      }
    }
  }

  return semanticScore;
};

// Main hybrid search function
export const hybridSearch = (
  query: string,
  items: SearchItem[],
  options: {
    synonymMap?: SynonymMap;
    filters?: SearchFilters;
    pinnedResults?: { keyword: string; url: string; title: string; description?: string; type: string; priority: number }[];
    limit?: number;
    typoTolerance?: boolean;
  } = {}
): SearchResult[] => {
  const {
    synonymMap = {},
    filters = {},
    pinnedResults = [],
    limit = 20,
    typoTolerance = true,
  } = options;

  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return [];

  const queryWords = normalizedQuery.split(/\s+/);

  // Score each item
  const scoredResults = items.map(item => {
    let score = 0;
    let matchType: SearchResult['matchType'] = 'contains';
    const highlights: HighlightedText[] = [];

    const titleLower = item.title.toLowerCase();
    const descLower = item.description.toLowerCase();
    const keywordsLower = item.keywords.map(k => k.toLowerCase());

    // 1. Exact title match (highest priority)
    if (titleLower === normalizedQuery) {
      score += 1000;
      matchType = 'exact';
    }
    // 2. Title starts with query
    else if (titleLower.startsWith(normalizedQuery)) {
      score += 500;
      matchType = 'prefix';
    }
    // 3. Title contains query
    else if (titleLower.includes(normalizedQuery)) {
      score += 200;
      matchType = 'contains';
      highlights.push(highlightField(item.title, normalizedQuery, 'title'));
    }
    // 4. Word-by-word title matching
    else {
      let wordMatchScore = 0;
      for (const word of queryWords) {
        if (titleLower.includes(word)) {
          wordMatchScore += 100;
          highlights.push(highlightField(item.title, word, 'title'));
        }
      }
      score += wordMatchScore;
    }

    // 5. Description matching
    if (descLower.includes(normalizedQuery)) {
      score += 100;
      highlights.push(highlightField(item.description, normalizedQuery, 'description'));
    } else {
      for (const word of queryWords) {
        if (word.length > 2 && descLower.includes(word)) {
          score += 30;
        }
      }
    }

    // 6. Keyword matching
    for (const keyword of keywordsLower) {
      if (keyword.includes(normalizedQuery)) {
        score += 75;
      } else {
        for (const word of queryWords) {
          if (keyword.includes(word)) {
            score += 25;
          }
        }
      }
    }

    // 7. Category matching
    if (item.category.toLowerCase().includes(normalizedQuery)) {
      score += 50;
    }

    // 8. Semantic matching (synonyms)
    const semanticScore = calculateSemanticScore(normalizedQuery, item, synonymMap);
    if (semanticScore > 0) {
      score += semanticScore;
      if (matchType === 'contains' && semanticScore > 50) {
        matchType = 'semantic';
      }
    }

    // 9. Typo tolerance (fuzzy matching) - only if no good matches found
    if (typoTolerance && score < 50) {
      const titleWords = titleLower.split(/\s+/);
      for (const queryWord of queryWords) {
        for (const titleWord of titleWords) {
          if (isSimilar(queryWord, titleWord)) {
            score += 40;
            matchType = 'fuzzy';
          }
        }
      }
    }

    // 10. Block type priority boost
    const typeBoost = BLOCK_TYPE_PRIORITY[item.type] || 20;
    score += typeBoost;

    // 11. Item priority boost
    score += item.priority * 10;

    return {
      ...item,
      score,
      matchType,
      highlights,
      isPinned: false,
    } as SearchResult;
  });

  // Filter by content type if specified
  let filteredResults = scoredResults.filter(r => r.score > 0);
  
  if (filters.contentType && filters.contentType.length > 0 && !filters.contentType.includes('all')) {
    filteredResults = filteredResults.filter(r => filters.contentType!.includes(r.type));
  }

  // Sort by score descending
  filteredResults.sort((a, b) => b.score - a.score);

  // Add pinned results at the top
  const pinnedMatches = pinnedResults
    .filter(p => normalizedQuery.includes(p.keyword.toLowerCase()) || p.keyword.toLowerCase().includes(normalizedQuery))
    .sort((a, b) => b.priority - a.priority)
    .map(p => ({
      id: `pinned-${p.url}`,
      title: p.title,
      description: p.description || '',
      category: 'Featured',
      type: p.type as SearchItem['type'],
      url: p.url,
      keywords: [],
      priority: 100,
      score: 2000,
      matchType: 'exact' as const,
      highlights: [],
      isPinned: true,
    }));

  // Combine pinned and regular results
  const finalResults = [...pinnedMatches, ...filteredResults];

  // Remove duplicates (keep pinned version)
  const seenUrls = new Set<string>();
  const dedupedResults = finalResults.filter(r => {
    if (seenUrls.has(r.url)) return false;
    seenUrls.add(r.url);
    return true;
  });

  return dedupedResults.slice(0, limit);
};

// Get content type icon
export const getContentTypeIcon = (type: string): string => {
  const icons: { [key: string]: string } = {
    product: '📦',
    page: '📄',
    feature: '⚡',
    category: '📁',
    blog: '📝',
    faq: '❓',
    'case-study': '📊',
    section: '📑',
  };
  return icons[type] || '📄';
};

// Generate snippet from description
export const generateSnippet = (
  description: string, 
  query: string, 
  maxLength = 150
): string => {
  const normalizedQuery = query.toLowerCase();
  const normalizedDesc = description.toLowerCase();
  
  const matchIndex = normalizedDesc.indexOf(normalizedQuery);
  
  if (matchIndex === -1 || matchIndex < maxLength / 2) {
    // No match or match is early - return from start
    return description.length > maxLength 
      ? description.slice(0, maxLength).trim() + '...'
      : description;
  }
  
  // Match is later in text - center snippet around match
  const start = Math.max(0, matchIndex - maxLength / 3);
  const end = Math.min(description.length, start + maxLength);
  
  let snippet = description.slice(start, end);
  if (start > 0) snippet = '...' + snippet;
  if (end < description.length) snippet = snippet.trim() + '...';
  
  return snippet;
};

// Normalize query for consistent analytics
export const normalizeQuery = (query: string): string => {
  return query
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '');
};
